import React, { useState } from 'react';
import { useLocation } from 'react-use';

import TrashIcon from 'assets/icons/trashCan.svg';
import Logo from 'assets/images/logo.svg';

import { EConsultantNewArticleType } from 'types/enums/EConsultant';
import { ERoute } from 'types/enums/ERoute';
import { EUrlSearchParam } from 'types/enums/EUrlSearchParam';
import { ESuluContentTypeText } from 'types/enums/sulu/EBackendData';

import navigateTo from 'utils/navigateTo';

export interface IConsultantArticleTile {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  contentType: EConsultantNewArticleType;
  url: string;
}

export interface IConsultantArticleTileProps {
  isThisMyProfile: boolean;
  tile: IConsultantArticleTile;
}

const ConsultantArticleTile = ({
  isThisMyProfile,
  tile,
}: IConsultantArticleTileProps) => {
  const { title, description, imageUrl, contentType, url, id } = tile;
  const { search: searchParamString } = useLocation();
  const searchParams = new URLSearchParams(searchParamString);
  const consultantId = searchParams.get(EUrlSearchParam.CONSULTANT_ID) ?? '';
  const [hover, setHover] = useState(false);

  const getResizedImageUrl = (originalUrl: string): string => {
    const dimensionRegex = /\/\d+x\d+\//;

    return originalUrl.replace(dimensionRegex, '/sulu-400x400-inset/');
  };
  const resizedImageUrl = getResizedImageUrl(imageUrl);

  const getBackgroundColor = (filter: EConsultantNewArticleType) => {
    switch (filter) {
      case EConsultantNewArticleType.PODCAST:
        return 'bg-consultant-tile-podcast/30';
      case EConsultantNewArticleType.VIDEO:
        return 'bg-consultant-tile-video/30';
      case EConsultantNewArticleType.ARTICLE:
      default:
        return 'bg-consultant-tile-article/30';
    }
  };

  const getBillText = (billText: EConsultantNewArticleType) => {
    switch (billText) {
      case EConsultantNewArticleType.PODCAST:
        return ESuluContentTypeText.PODCAST;
      case EConsultantNewArticleType.VIDEO:
        return ESuluContentTypeText.VIDEO;
      case EConsultantNewArticleType.ARTICLE:
      default:
        return ESuluContentTypeText.ARTICLE;
    }
  };

  const bgImageStyle = {
    backgroundImage: `url(${resizedImageUrl})`,
  };
  const backgroundColor = getBackgroundColor(contentType);
  const bill = getBillText(contentType);

  const handleClick = () => {
    window.open(url, '_self');
  };

  const handleTrashClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isThisMyProfile) {
      return;
    }
    navigateTo(ERoute.CONSULTANT_ARTICLE, [
      { key: EUrlSearchParam.CONSULTANT_ID, value: consultantId },
      { key: EUrlSearchParam.ARTICLE_ID, value: id },
      {
        key: EUrlSearchParam.CONSULTANT_ARTICLE_SHOULD_BE_DELETED,
        value: 'true',
      },
    ]);
  };

  return (
    <div
      className={`w-52 p-2.5 rounded-xl cursor-pointer overflow-hidden ${backgroundColor}`}
      onClick={handleClick}
      onMouseOver={() => setHover(true)}
      onFocus={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      onBlur={() => setHover(false)}
      onKeyDown={e => e.key === 'Enter' && handleClick()}
      role='button'
      tabIndex={0}
    >
      <div className='relative pt-[100%] rounded-md shadow-md'>
        {bgImageStyle.backgroundImage ? (
          <div
            className={`absolute inset-0 rounded-md bg-center bg-no-repeat bg-cover transition-all
    ${hover ? 'brightness-50' : 'brightness-100'}`}
            style={bgImageStyle}
          />
        ) : (
          <div
            className={`absolute inset-0 rounded-md bg-center bg-no-repeat bg-cover bg-gradient-to-t from-[#B5B5B5] to-[#E4E3E5] transition-all
    ${hover ? 'brightness-50' : 'brightness-100'}`}
          >
            <Logo className='w-[75%] h-[50%] mt-12 mx-auto fill-white' />
          </div>
        )}
        {contentType && (
          <div
            className={`flex text-black bg-white/90 px-2.5 py-0.5 rounded-full absolute top-2 left-2 transition-opacity ${
              hover ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <span className='text-xs'>{bill}</span>
          </div>
        )}
      </div>

      <div className='relative flex items-center h-12 px-2 mt-2'>
        <p
          className={`absolute left-2 right-2 inset-y-0 font-bold line-clamp-2 transition-all ${
            hover ? 'text-white' : ''
          }`}
          style={{
            transform: hover ? 'translateY(calc(-100% - 1rem))' : '',
          }}
        >
          {title}
        </p>
        <p
          className={`text-black text-sm line-clamp-2 transition-opacity ${
            hover ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {description}
        </p>
      </div>
      {isThisMyProfile && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div
          className='flex items-center justify-center rounded-full ml-auto mt-2 w-8 h-8 bg-orange hover:bg-gradient-to-r hover:from-orange-gradient-from to-orange-gradient-to'
          onClick={handleTrashClick}
        >
          <TrashIcon />
        </div>
      )}
    </div>
  );
};

export default ConsultantArticleTile;
